import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  VideoLibrary as VideoIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../config/adminConfig';
import { useNavigate } from 'react-router-dom';

interface DeviceInfo {
  ip: string;
  userAgent: string;
  acceptLanguage: string;
  platform: string;
  browser: string;
  deviceType: string;
  screenResolution: string;
  timezone: string;
  timestamp: string;
}

interface UserStats {
  id: string;
  name: string;
  email: string;
  status: 'online' | 'offline';
  lastLogin: string;
  stayDuration: string; // 停留時長 (hh:mm)
  videoCount: number;
  checkpointCount: number;
  loginTime: string;
  deviceInfo?: DeviceInfo | null;
}

interface AdminStats {
  totalUsers: number;
  onlineUsers: number;
  totalVideos: number;
  totalCheckpoints: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [adminStats, setAdminStats] = useState<AdminStats>({
    totalUsers: 0,
    onlineUsers: 0,
    totalVideos: 0,
    totalCheckpoints: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'stayDuration' | 'lastLogin' | 'videoCount' | 'checkpointCount'>('stayDuration');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 檢查是否為管理員
  const isAdmin = isAdminUser(user?.email, user?.name);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminData();
      // 移除自動刷新，改為手動刷新
    }
  }, [isAdmin]);


  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🔧 修復：使用 API 獲取數據以確保在線狀態準確
      const response = await fetch('/api/admin/user-stats');
      if (!response.ok) {
        throw new Error(`API 請求失敗: ${response.status}`);
      }
      
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || '獲取數據失敗');
      }

      // 轉換 API 數據格式
      const userStatsData: UserStats[] = data.userStats.map((user: any) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        status: user.status,
        lastLogin: user.lastLogin,
        stayDuration: user.stayDuration || '00:00',
        videoCount: user.videoCount || 0,
        checkpointCount: user.checkpointCount || 0,
        loginTime: user.loginTime || user.lastLogin
      }));

      setUserStats(userStatsData);
      setAdminStats({
        totalUsers: data.adminStats.totalUsers,
        onlineUsers: data.adminStats.onlineUsers,
        totalVideos: data.adminStats.totalVideos,
        totalCheckpoints: data.adminStats.totalCheckpoints
      });
    } catch (err) {
      console.error('獲取管理數據錯誤:', err);
      setError(err instanceof Error ? err.message : '獲取數據時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const parseStayDuration = (duration: string): number => {
    const [hours, minutes] = duration.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 計算實時停留時間（使用當前時間而不是緩存的時間）
  const calculateRealTimeStayDuration = (loginTime: string, status: string): string => {
    if (status !== 'online') {
      return '---';
    }
    
    if (!loginTime || loginTime === '') {
      return '00:00';
    }
    
    try {
      const login = new Date(loginTime);
      if (isNaN(login.getTime())) {
        console.warn('⚠️ 無效的登入時間:', loginTime);
        return '00:00';
      }
      
      // 使用當前時間而不是緩存的時間
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - login.getTime()) / 1000);
      
      // 如果時間差為負數（未來時間），返回 00:00
      if (diffInSeconds < 0) {
        return '00:00';
      }
      
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;
      
      // 限制最大顯示時間為 99:59
      const displayHours = Math.min(hours, 99);
      const displayMinutes = Math.min(minutes, 59);
      
      return `${displayHours.toString().padStart(2, '0')}:${displayMinutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.warn('⚠️ 計算停留時間失敗:', error, loginTime);
      return '00:00';
    }
  };

  const handleSort = (property: typeof sortBy) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const sortedUserStats = [...userStats].sort((a, b) => {
    // 首先按在線狀態排序：在線用戶排前面
    if (a.status === 'online' && b.status !== 'online') {
      return -1; // a 在線，b 離線，a 排前面
    }
    if (a.status !== 'online' && b.status === 'online') {
      return 1; // a 離線，b 在線，b 排前面
    }
    
    // 如果狀態相同，則按最後登入時間由近到遠排列
    const aLastLoginTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
    const bLastLoginTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
    
    // 由近到遠：時間大的（較新的）排前面，無效日期視為 0
    return bLastLoginTime - aLastLoginTime;
  });

  const formatStayDuration = (duration: string): string => {
    return duration;
  };

  const formatLastLogin = (lastLogin: string): string => {
    if (!lastLogin || lastLogin === '') {
      return '從未登入';
    }
    
    const date = new Date(lastLogin);
    if (isNaN(date.getTime())) {
      return '日期格式錯誤';
    }
    
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto' }}>
          <Typography variant="h6">權限不足</Typography>
          <Typography>您沒有權限訪問此頁面</Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>載入管理數據中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 頁面標題 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AdminIcon sx={{ fontSize: 32, color: '#d32f2f', mr: 1 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          系統管理員
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="返回首頁">
            <IconButton 
              onClick={() => navigate('/')}
              color="default"
              sx={{ 
                backgroundColor: '#f5f5f5',
                color: '#666',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                },
                px: 2,
                py: 1,
                borderRadius: 2
              }}
            >
              <HomeIcon sx={{ mr: 1 }} />
              首頁
            </IconButton>
          </Tooltip>
          <Tooltip title="更新數據">
            <IconButton 
              onClick={fetchAdminData}
              color="primary"
              sx={{ 
                backgroundColor: '#1976d2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                px: 2,
                py: 1,
                borderRadius: 2
              }}
            >
              <RefreshIcon sx={{ mr: 1 }} />
              更新
            </IconButton>
          </Tooltip>
          <Tooltip title="使用者管理">
            <IconButton 
              onClick={() => {
                // 打開新視窗進行用戶管理
                const newWindow = window.open(
                  '/user-management-window',
                  'userManagement',
                  'width=1200,height=800,scrollbars=yes,resizable=yes,status=yes,location=yes'
                );
                
                if (newWindow) {
                  console.log('✅ 用戶管理視窗已打開');
                } else {
                  console.error('❌ 無法打開用戶管理視窗，請檢查彈出視窗阻擋設定');
                  alert('無法打開用戶管理視窗，請允許彈出視窗並重試');
                }
              }}
              color="default"
            >
              <PeopleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="管理員管理">
            <IconButton onClick={() => navigate('/admin-management')}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* 統計卡片 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {adminStats.totalUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    總用戶數
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {adminStats.onlineUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    在線用戶
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <VideoIcon sx={{ fontSize: 40, color: '#ff9800', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {adminStats.totalVideos}
                  </Typography>
                  <Typography color="text.secondary">
                    總影片數
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ fontSize: 40, color: '#9c27b0', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {adminStats.totalCheckpoints}
                  </Typography>
                  <Typography color="text.secondary">
                    總重點數
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 錯誤提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 用戶列表表格 */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  #
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  ID (影片數/重點數)
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  Status
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'stayDuration'}
                    direction={sortBy === 'stayDuration' ? sortOrder : 'asc'}
                    onClick={() => handleSort('stayDuration')}
                  >
                    停留時長
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'lastLogin'}
                    direction={sortBy === 'lastLogin' ? sortOrder : 'asc'}
                    onClick={() => handleSort('lastLogin')}
                  >
                    最後登入
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  設備信息
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUserStats.map((user, index) => (
                <TableRow
                  key={user.id}
                  sx={{
                    backgroundColor: user.status === 'online' ? '#fff' : '#f9f9f9',
                    color: user.status === 'online' ? '#000' : '#666'
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {user.name}({user.videoCount}/{user.checkpointCount})
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status === 'online' ? '在線' : user.status === 'offline' ? '離線' : '未知'}
                      color={user.status === 'online' ? 'success' : user.status === 'offline' ? 'default' : 'warning'}
                      size="small"
                      sx={{
                        backgroundColor: user.status === 'online' ? '#4caf50' : user.status === 'offline' ? '#e0e0e0' : '#ff9800',
                        color: user.status === 'online' ? '#fff' : user.status === 'offline' ? '#666' : '#fff'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      {calculateRealTimeStayDuration(user.loginTime, user.status)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {formatLastLogin(user.lastLogin)}
                  </TableCell>
                  <TableCell>
                    {user.deviceInfo ? (
                      <Box sx={{ fontSize: '0.75rem' }}>
                        <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {user.deviceInfo.platform} / {user.deviceInfo.browser}
                        </Box>
                        <Box sx={{ color: 'text.secondary' }}>
                          {user.deviceInfo.deviceType} • {user.deviceInfo.screenResolution}
                        </Box>
                        <Box sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          IP: {user.deviceInfo.ip} • {user.deviceInfo.timezone}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        無設備信息
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 空狀態 */}
      {sortedUserStats.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            暫無用戶數據
          </Typography>
        </Box>
      )}

    </Box>
  );
};

export default AdminDashboard;
